/**
 * PerkCatalog — all perk definitions with their effects.
 *
 * Perks are binary (one-time) or stackable. Effects are registered
 * as ModifierBag modifiers and/or hooks at battle start.
 *
 * Weight controls how often a perk appears in the level-up choices.
 * Higher weight = more common. Roughly:
 *   10 = common, 6 = uncommon, 3 = rare
 */

import type { PerkDef } from "./PerkDef";
import * as S from "../stats/StatKeys";
import * as H from "../stats/HookKeys";

export const PERK_LUCKY_START: PerkDef = {
  id: "lucky_start",
  name: "Lucky Start",
  description: "Start each battle with +1 AP",
  color: "#fbbf24",
  icon: "!",
  stackable: false,
  maxStacks: 1,
  weight: 6,
  apply: (bag, _stacks) => {
    bag.add(S.AP_MAX_FLAT, "flat", 1, "Lucky Start");
  },
};

export const PERK_IRON_WILL: PerkDef = {
  id: "iron_will",
  name: "Iron Will",
  description: "Survive one lethal hit per battle with 1 HP",
  color: "#ef4444",
  icon: "#",
  stackable: false,
  maxStacks: 1,
  weight: 3,
  apply: (bag, _stacks) => {
    bag.addHook(H.ON_DAMAGE_TAKEN, (ctx: any) => {
      return { ironWill: true };
    }, "Iron Will");
  },
};

export const PERK_SCAVENGER: PerkDef = {
  id: "scavenger",
  name: "Scavenger",
  description: "Find +2 extra gold after each battle",
  color: "#fbbf24",
  icon: "$",
  stackable: true,
  maxStacks: 5,
  weight: 10,
  apply: (bag, stacks) => {
    bag.add(S.GOLD_PERCENT, "flat", stacks * 2, `Scavenger x${stacks}`);
  },
  getValue: (stacks, key) => {
    if (key === "bonusGold") return stacks * 2;
    return 0;
  },
  descFn: (stacks) => `+${stacks * 2} gold per battle`,
};

export const PERK_SHARP_MIND: PerkDef = {
  id: "sharp_mind",
  name: "Sharp Mind",
  description: "Score thresholds are 2 points easier to reach",
  color: "#3b82f6",
  icon: "*",
  stackable: true,
  maxStacks: 5,
  weight: 6,
  apply: null, // Threshold reduction is read directly by DamageCalculator
  getValue: (stacks, key) => {
    if (key === "thresholdReduction") return stacks * 2;
    return 0;
  },
  descFn: (stacks) => `Thresholds −${stacks * 2} points`,
};

export const PERK_SECOND_WIND: PerkDef = {
  id: "second_wind",
  name: "Second Wind",
  description: "Heal 3 HP when you defeat an enemy",
  color: "#22c55e",
  icon: "+",
  stackable: true,
  maxStacks: 5,
  weight: 8,
  apply: (bag, stacks) => {
    const healAmount = stacks * 3;
    bag.addHook(H.ON_ENEMY_KILLED, (_ctx: any) => {
      return { heal: healAmount };
    }, `Second Wind x${stacks}`);
  },
  descFn: (stacks) => `+${stacks * 3} HP on kill`,
};

export const PERK_BERSERKER: PerkDef = {
  id: "berserker",
  name: "Berserker",
  description: "+2 ATK when below 50% HP",
  color: "#ef4444",
  icon: "X",
  stackable: false,
  maxStacks: 1,
  weight: 3,
  apply: (bag, _stacks) => {
    bag.addHook(H.ON_WORD_SCORED, (ctx: any) => {
      if (ctx.playerHpPercent !== undefined && ctx.playerHpPercent < 0.5) {
        return { damage: (ctx.damage ?? 0) + 2 };
      }
    }, "Berserker");
  },
};

export const PERK_RESURRECT: PerkDef = {
  id: "resurrect",
  name: "Resurrect",
  description: "Used tiles have a 15% chance to come back with a new letter",
  color: "#06b6d4",
  icon: "R",
  stackable: true,
  maxStacks: 5,
  weight: 6,
  apply: null, // Read directly by BattleScene after word submission
  getValue: (stacks, key) => {
    if (key === "resurrectChance") return stacks * 0.15;
    return 0;
  },
  descFn: (stacks) => `${stacks * 15}% chance per tile to revive`,
};

export const PERK_CHAIN_LINK: PerkDef = {
  id: "chain_link",
  name: "Chain Link",
  description: "+10% adjacency bonus per pair",
  color: "#22c55e",
  icon: "~",
  stackable: true,
  maxStacks: 5,
  weight: 6,
  apply: null, // Adjacency bonus is read directly by BattleScene
  getValue: (stacks, key) => {
    if (key === "adjBonusPerPair") return stacks * 0.1;
    return 0;
  },
  descFn: (stacks) => {
    const rate = 10 + stacks * 10;
    return `+${stacks * 10}%/pair adj bonus (total: ${rate}%/pair)`;
  },
};

/** All perk definitions for the catalog. */
export const ALL_PERKS: PerkDef[] = [
  PERK_LUCKY_START,
  PERK_IRON_WILL,
  PERK_SCAVENGER,
  PERK_SHARP_MIND,
  PERK_SECOND_WIND,
  PERK_BERSERKER,
  PERK_RESURRECT,
  PERK_CHAIN_LINK,
];

/** Lookup by ID. */
export const PERKS_BY_ID: ReadonlyMap<string, PerkDef> = new Map(
  ALL_PERKS.map(p => [p.id, p])
);
